import { API } from "../../../api/api";
import { getAuthHeaders } from "../../../api/http";
import type { ChatMessage, StreamChunk } from "../domain/types";

export async function streamChat(
  messages: ChatMessage[],
  token: string,
  pageContext: Record<string, unknown> | null,
  onChunk: (chunk: StreamChunk) => void,
  onDone: () => void,
  signal?: AbortSignal
): Promise<void> {
  const apiMessages = messages.map((m) => ({
    role: m.role,
    content: m.content,
  }));

  const response = await fetch(`${API}/assistant/chat`, {
    method: "POST",
    headers: getAuthHeaders(token),
    body: JSON.stringify({
      messages: apiMessages,
      page_context: pageContext,
    }),
    signal,
  });

  if (!response.ok) {
    const text = await response.text();
    onChunk({ type: "error", error: text || `Error: ${response.status}` });
    onDone();
    return;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    onChunk({ type: "error", error: "No response body" });
    onDone();
    return;
  }

  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      // Keep the last potentially incomplete line in the buffer
      buffer = lines.pop() || "";

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith("data: ")) continue;

        const data = trimmed.slice(6);
        if (data === "[DONE]") {
          onDone();
          return;
        }

        try {
          const chunk: StreamChunk = JSON.parse(data);
          onChunk(chunk);
        } catch {
          // Skip malformed chunks
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  onDone();
}
